import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  createFilterOptions,
  Dialog,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { withStyles } from "@mui/styles";

import EditIcon from "@mui/icons-material/Edit";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CallIcon from "@mui/icons-material/Call";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PaymentIcon from "@mui/icons-material/Payment";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ShareIcon from "@mui/icons-material/Share";

import moment from "moment";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";

import {
  assignUserRoute,
  baseDomain,
  participantsAddSeedPlayerRoute,
  participantsAssignOrganizationMemberRoute,
  participantsConvertOrganizationMemberToManualRoute,
  participantsDeleteRoute,
  participantsRestoreWithdrawnRoute,
  participantsShowRoute,
  participantsUpdateRoute,
  playerUpdateRoute,
  searchUserRoute,
  tournamentClaimGenerateRoute,
} from "../../../network/api-routes";
import performRequest from "../../../network/perform-request";
import { fetchSportCountries } from "../../../network/sport-countries";
import UserContext from "../../../context/UserContext";
import {
  TOURNAMENT_FINISHED,
  TOURNAMENT_PENDING,
  TOURNAMENT_PENDING_FINISHED,
} from "../../../constants/tournamentStates";

import AlertDialog from "../../common/AlertDialog";
import RevaText from "components/common/RevaText";
import ChangeTournamentModal from "./ChangeTournamentModal";
import ProfilesModal from "./ProfilesModal";
import TournamentMemberSelector from "./TournamentMemberSelector";
import { suggestSurnameName } from "./participantNameUtils";

// The input shows only the code once selected, so search by code and name.
const filterNationalities = createFilterOptions({ stringify: (country) => `${country.code} ${country.name}` });

const styles = (theme) => ({
  modalContainer: {
    width: "100%",
    padding: theme.spacing(3),
    margin: "auto",
    borderRadius: 5,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
  },
  playerCard: {
    padding: theme.spacing(1.5),
    borderRadius: 16,
    backgroundColor: theme.palette.background.base,
  },
  metaRow: {
    color: theme.palette.text.secondary,
  },
});

const PaymentStatusChip = ({ status, paidOnline }) => {
  const intl = useIntl();
  if (paidOnline) {
    return (
      <Chip size="small" color="primary" icon={<PaymentIcon />} label={intl.formatMessage({ id: "Pagado en línea" })} />
    );
  }
  if (status === "completed") {
    return (
      <Chip size="small" color="success" icon={<DoneAllIcon />} label={intl.formatMessage({ id: "Pago confirmado" })} />
    );
  }
  return (
    <Chip
      size="small"
      icon={<HourglassEmptyIcon color="disabled" />}
      label={intl.formatMessage({ id: "Pago pendiente" })}
    />
  );
};

const PlayerCard = ({
  player = null,
  participant,
  canSearchUsers,
  onPlayerUpdated,
  classes,
  isSingles, // Nueva prop para detectar la modalidad del torneo
  NoUser,
  isOrganizationTournament,
  requiresMembership,
  tournamentId,
}) => {
  const intl = useIntl();
  const { user: authUser } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const isAppUser = Boolean(player?.playerable_type === "App\\User" && player?.playerable_id);
  const assignedTournamentMember = (participant?.tournament_members || []).find((assignment) =>
    isSingles ? assignment?.player_id == null : String(assignment?.player_id) === String(player?.id),
  );
  const organizationMember =
    player?.playerable_type === "App\\OrganizationMember"
      ? player?.playerable
      : assignedTournamentMember?.organization_member;
  const organizationMemberId = organizationMember?.id || assignedTournamentMember?.organization_member_id;
  const isOrganizationMember = Boolean(organizationMemberId);
  const hasRevaAccount = Boolean(isAppUser || (isOrganizationMember && organizationMember?.user_id));

  const playerName = player?.name || player?.full_name || "";
  const playerPhone = player?.phone_number || player?.cellphone_number || "";
  const playerDoc = player?.document_number || "";

  const [mode, setMode] = useState(null); // null | "edit" | "link" | "member" | "manual"
  const [saving, setSaving] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [sharingInvite, setSharingInvite] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [confirmInactiveMember, setConfirmInactiveMember] = useState(false);

  const [name, setName] = useState(player.name || "");
  const [phoneNumber, setPhoneNumber] = useState(player.phone_number || "");
  const [documentNumber, setDocumentNumber] = useState(player.document_number || "");

  const [searchPhone, setSearchPhone] = useState("");
  const [searchDoc, setSearchDoc] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchEmpty, setSearchEmpty] = useState(false);

  useEffect(() => {
    setName(player.name || "");
    setPhoneNumber(player.phone_number || "");
    setDocumentNumber(player.document_number || "");
  }, [player]);

  const closeInline = () => {
    setMode(null);
    setSelectedMembers([]);
    setSearchResult(null);
    setSearchEmpty(false);
    setSearchPhone("");
    setSearchDoc("");
  };

  const saveAsManualPlayer = () => {
    setSaving(true);
    performRequest(
      "POST",
      participantsConvertOrganizationMemberToManualRoute,
      {
        participant_id: participant.id,
        player_id: isSingles ? null : player?.id,
        expected_organization_member_id: organizationMemberId,
        name,
        phone_number: phoneNumber,
        document_number: documentNumber,
      },
      true,
      authUser,
    )
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Jugador convertido a manual" }), { variant: "success" });
        setSaving(false);
        closeInline();
        onPlayerUpdated();
      })
      .catch((error) => {
        setSaving(false);
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const savePlayer = () => {
    if (hasRevaAccount) return;
    setSaving(true);
    performRequest(
      "POST",
      playerUpdateRoute,
      {
        player_id: player.id,
        participant_id: participant.id,
        name,
        phone_number: phoneNumber,
        document_number: documentNumber,
      },
      true,
      authUser,
    )
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Jugador actualizado" }), { variant: "success" });
        setSaving(false);
        closeInline();
        onPlayerUpdated();
      })
      .catch((error) => {
        setSaving(false);
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const searchUser = () => {
    setSearching(true);
    setSearchResult(null);
    setSearchEmpty(false);
    performRequest("POST", searchUserRoute, { phone_number: searchPhone, document_number: searchDoc }, true, authUser)
      .then((response) => {
        setSearching(false);
        setSearchResult(response.data.data);
        setSearchEmpty(!response.data.data);
      })
      .catch((error) => {
        setSearching(false);
        setSearchResult(null);
        if (error?.response?.status === 404) {
          setSearchEmpty(true);
        } else {
          enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
        }
      });
  };

  const assignUser = () => {
    setSaving(true);
    performRequest(
      "POST",
      assignUserRoute,
      {
        user_id: searchResult.id,
        player_id: player?.id || null,
        participant_id: participant.id,
      },
      true,
      authUser,
    )
      .then((response) => {
        enqueueSnackbar(response.data.message, { variant: "success" });
        setSaving(false);
        closeInline();
        onPlayerUpdated();
      })
      .catch((error) => {
        setSaving(false);
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const unlinkRevaAccount = () => {
    setSaving(true);
    performRequest(
      "POST",
      playerUpdateRoute,
      {
        player_id: player.id,
        participant_id: participant.id,
        playerable_type: null,
        playerable_id: null,
      },
      true,
      authUser,
    )
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Cuenta Reva desasociada" }), { variant: "success" });
        setSaving(false);
        onPlayerUpdated();
      })
      .catch((error) => {
        setSaving(false);
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const hasPhone = Boolean(playerPhone);

  const copyInviteLink = async (link) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(link);
      return;
    }

    const input = document.createElement("textarea");
    input.value = link;
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
  };

  const invitePlayer = async () => {
    setSharingInvite(true);
    try {
      const response = await performRequest(
        "POST",
        tournamentClaimGenerateRoute,
        {
          tournament_id: participant.tournament_id || participant.tournament?.id,
          participant_id: participant.id,
          player_id: isSingles && NoUser ? null : player?.id,
          source: "tournament_panel",
        },
        true,
        authUser,
      );
      const link = response?.data?.data?.claim_url;
      if (!link) throw new Error("No se recibió el enlace de invitación");

      const message = `Te invito a vincularte a la inscripción de ${participant.name} en Reva.`;
      if (navigator.share) {
        await navigator.share({ title: "Invitación a torneo", text: message, url: link });
      } else {
        await copyInviteLink(link);
        enqueueSnackbar(intl.formatMessage({ id: "Enlace de invitación copiado" }), { variant: "success" });
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        enqueueSnackbar(error?.response?.data?.message || "No se pudo preparar la invitación.", { variant: "error" });
      }
    } finally {
      setSharingInvite(false);
    }
  };

  const assignOrganizationMember = async (allowInactiveMember = false) => {
    const member = selectedMembers[0];
    if (!member) return;

    if (!allowInactiveMember && !member.is_active) {
      setConfirmInactiveMember(true);
      return;
    }

    setSaving(true);
    setConfirmInactiveMember(false);
    try {
      await performRequest(
        "POST",
        participantsAssignOrganizationMemberRoute,
        {
          participant_id: participant.id,
          player_id: isSingles && NoUser ? null : player?.id,
          organization_member_id: member.organization_member_id,
          expected_organization_member_id: isOrganizationMember ? organizationMemberId : null,
          allow_inactive_members: allowInactiveMember,
        },
        true,
        authUser,
      );
      enqueueSnackbar(
        intl.formatMessage({
          id: isOrganizationMember ? "Miembro reemplazado en la inscripción" : "Miembro vinculado a la inscripción",
        }),
        { variant: "success" },
      );
      setSelectedMembers([]);
      closeInline();
      onPlayerUpdated();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "No se pudo vincular el miembro.", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const inactiveMemberDialog = (
    <AlertDialog
      hideButton
      isOpen={confirmInactiveMember}
      onConfirm={() => assignOrganizationMember(true)}
      onCancel={() => setConfirmInactiveMember(false)}
      alertTitle={intl.formatMessage({ id: "Miembro inactivo" })}
      alertBody={intl.formatMessage({
        id: "El miembro seleccionado está inactivo. ¿Deseas vincularlo de todas formas?",
      })}
      confirmButtonTitle={intl.formatMessage({ id: "Vincular igualmente" })}
      cancelButtonTitle={intl.formatMessage({ id: "Cancelar" })}
    />
  );

  const manualPlayerEditor = (
    <Collapse in={mode === "manual"} unmountOnExit>
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Divider />
        <Alert severity="warning">
          {intl.formatMessage({
            id: "Este jugador dejará de representar al miembro en esta inscripción. La cuenta Reva del miembro no será modificada.",
          })}
        </Alert>
        <TextField
          size="small"
          label={intl.formatMessage({ id: "Nombre" })}
          value={name}
          onChange={(event) => setName(event.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          label={intl.formatMessage({ id: "Teléfono" })}
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          label={intl.formatMessage({ id: "Documento de identidad" })}
          value={documentNumber}
          onChange={(event) => setDocumentNumber(event.target.value)}
          fullWidth
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={closeInline} disabled={saving}>
            {intl.formatMessage({ id: "Cancelar" })}
          </Button>
          <Button variant="contained" onClick={saveAsManualPlayer} disabled={saving || !name.trim()}>
            {saving ? <CircularProgress size={18} /> : intl.formatMessage({ id: "Guardar como jugador manual" })}
          </Button>
        </Stack>
      </Stack>
    </Collapse>
  );

  // VISTA ESPECIAL PARA MODALIDAD SINGLES
  if (isSingles && NoUser) {
    return (
      <Paper variant="outlined" className={classes.playerCard}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
            <Avatar sx={{ width: 48, height: 48 }} src={player.full_profile_pic_url} alt={participant.name} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <RevaText sx={{ fontWeight: 500 }}>{participant.name}</RevaText>
              {player?.ranking_points !== undefined && player?.ranking_points !== null && (
                <Chip
                  size="small"
                  color="secondary"
                  variant="outlined"
                  icon={<EmojiEventsIcon />}
                  label={`${intl.formatMessage({ id: "Puntos" })}: ${player.ranking_points}`}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={intl.formatMessage({ id: "Invitar con enlace" })}>
              <Button size="small" startIcon={<ShareIcon />} onClick={invitePlayer} disabled={sharingInvite}>
                {sharingInvite ? <CircularProgress size={16} /> : intl.formatMessage({ id: "Invitar" })}
              </Button>
            </Tooltip>
            {isOrganizationTournament && (
              <Tooltip
                title={intl.formatMessage({ id: isOrganizationMember ? "Cambiar miembro" : "Vincular miembro" })}
              >
                <IconButton
                  size="small"
                  onClick={() => setMode(mode === "member" ? null : "member")}
                  color={mode === "member" ? "primary" : "default"}
                >
                  <VerifiedUserIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isOrganizationMember && !requiresMembership && (
              <Tooltip title={intl.formatMessage({ id: "Usar datos manuales" })}>
                <IconButton
                  size="small"
                  onClick={() => setMode(mode === "manual" ? null : "manual")}
                  color={mode === "manual" ? "primary" : "default"}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canSearchUsers && !hasRevaAccount && !isOrganizationMember && (
              <Tooltip title={intl.formatMessage({ id: "Vincular cuenta Reva" })}>
                <IconButton
                  size="small"
                  onClick={() => setMode(mode === "link" ? null : "link")}
                  color={mode === "link" ? "primary" : "default"}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canSearchUsers && isAppUser && !isOrganizationMember && !requiresMembership && (
              <Tooltip title={intl.formatMessage({ id: "Desvincular Cuenta Reva" })}>
                <IconButton size="small" onClick={() => setConfirmUnlink(true)}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Mantenemos el colapso de búsqueda intacto para que la vinculación funcione */}
        <Collapse in={mode === "link"} unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <RevaText
              variant="caption"
              className={classes.metaRow}
              id="Introduce el número de teléfono o documento para buscar un usuario de Reva y vincularlo a este jugador."
              sx={{ mb: 1, display: "block" }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                fullWidth
                label={intl.formatMessage({ id: "Teléfono" })}
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchUser()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={searchUser}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                fullWidth
                label={intl.formatMessage({ id: "Documento de identidad" })}
                value={searchDoc}
                onChange={(e) => setSearchDoc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchUser()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={searchUser}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            {searching && (
              <Stack alignItems="center" sx={{ mt: 2 }}>
                <CircularProgress size={20} />
              </Stack>
            )}
            {searchEmpty && !searching && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {intl.formatMessage({
                  id: "No se encontró un usuario Reva con esos datos",
                })}
              </Alert>
            )}
            {searchResult && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                <Avatar src={searchResult.full_profile_pic_url} />
                <RevaText sx={{ flex: 1 }}>{searchResult.full_name}</RevaText>
                <Button variant="contained" onClick={assignUser} disabled={saving}>
                  {intl.formatMessage({ id: "Vincular usuario" })}
                </Button>
              </Stack>
            )}
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={closeInline}>{intl.formatMessage({ id: "Cancelar" })}</Button>
            </Stack>
          </Box>
        </Collapse>

        <Collapse in={mode === "member"} unmountOnExit>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Divider />
            <TournamentMemberSelector
              tournamentId={tournamentId}
              value={selectedMembers}
              onChange={setSelectedMembers}
              maxSelections={1}
              label={intl.formatMessage({ id: "Seleccionar miembro" })}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button onClick={closeInline}>{intl.formatMessage({ id: "Cancelar" })}</Button>
              <Button
                variant="contained"
                onClick={() => assignOrganizationMember()}
                disabled={!selectedMembers.length || saving}
              >
                {saving ? (
                  <CircularProgress size={18} />
                ) : (
                  intl.formatMessage({ id: isOrganizationMember ? "Cambiar miembro" : "Vincular miembro" })
                )}
              </Button>
            </Stack>
            {isOrganizationMember && !requiresMembership && (
              <Button size="small" onClick={() => setMode("manual")}>
                {intl.formatMessage({ id: "No encuentro al miembro; usar datos manuales" })}
              </Button>
            )}
          </Stack>
        </Collapse>

        {manualPlayerEditor}

        <AlertDialog
          hideButton
          isOpen={confirmUnlink}
          onConfirm={() => {
            setConfirmUnlink(false);
            unlinkRevaAccount();
          }}
          onCancel={() => setConfirmUnlink(false)}
          alertBody={intl.formatMessage({
            id: "¿Estás seguro de desvincular la cuenta de este jugador? Ya no podrá ver los resultados del torneo en su app.",
          })}
          confirmButtonTitle={intl.formatMessage({ id: "Sí, estoy seguro" })}
          cancelButtonTitle={intl.formatMessage({ id: "Volver atrás" })}
        />
        {inactiveMemberDialog}
      </Paper>
    );
  }

  // VISTA NORMAL (DOUBLES / EQUIPOS)
  return (
    <Paper variant="outlined" className={classes.playerCard}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={0}>
          <Avatar sx={{ width: 48, height: 48 }} src={player?.full_profile_pic_url} alt={playerName} />
          <Stack minWidth={0} gap={0.5}>
            <RevaText sx={{ fontWeight: 500 }}>{playerName}</RevaText>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {isOrganizationMember ? (
                <>
                  <Chip
                    size="small"
                    color={organizationMember?.is_active ? "secondary" : "warning"}
                    variant="outlined"
                    icon={<VerifiedUserIcon />}
                    label={intl.formatMessage({
                      id: organizationMember?.is_active ? "Miembro activo" : "Miembro inactivo",
                    })}
                  />
                  {hasRevaAccount && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      icon={<VerifiedUserIcon />}
                      label={intl.formatMessage({ id: "Cuenta Reva" })}
                    />
                  )}
                </>
              ) : hasRevaAccount ? (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  icon={<VerifiedUserIcon />}
                  label={intl.formatMessage({ id: "Cuenta Reva" })}
                />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<PersonOutlineIcon />}
                  label={intl.formatMessage({ id: "Jugador manual" })}
                />
              )}
              {!hasPhone && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={intl.formatMessage({ id: "Sin teléfono" })}
                />
              )}
              {player?.ranking_points !== undefined && player?.ranking_points !== null && (
                <Chip
                  size="small"
                  color="secondary"
                  variant="outlined"
                  icon={<EmojiEventsIcon />}
                  label={`${intl.formatMessage({ id: "Puntos" })}: ${player.ranking_points}`}
                />
              )}
            </Stack>
            {hasPhone && (
              <RevaText variant="caption" className={classes.metaRow}>
                {playerPhone}
              </RevaText>
            )}
            {playerDoc && (
              <RevaText variant="caption" className={classes.metaRow}>
                {`${intl.formatMessage({ id: "Documento" })}: ${playerDoc}`}
              </RevaText>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {hasPhone && (
            <>
              <Tooltip title={intl.formatMessage({ id: "Llamar" })}>
                <IconButton size="small" component="a" href={`tel:${playerPhone}`}>
                  <CallIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="WhatsApp">
                <IconButton
                  size="small"
                  component="a"
                  href={`https://wa.me/${playerPhone.replace("+", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {!hasRevaAccount && (
            <>
              <Tooltip title={intl.formatMessage({ id: "Invitar con enlace" })}>
                <Button size="small" startIcon={<ShareIcon />} onClick={invitePlayer} disabled={sharingInvite}>
                  {sharingInvite ? <CircularProgress size={16} /> : intl.formatMessage({ id: "Invitar" })}
                </Button>
              </Tooltip>
              {!isOrganizationMember && (
                <Tooltip title={intl.formatMessage({ id: "Editar" })}>
                  <IconButton
                    size="small"
                    onClick={() => setMode(mode === "edit" ? null : "edit")}
                    color={mode === "edit" ? "primary" : "default"}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {isOrganizationTournament && (
            <Tooltip title={intl.formatMessage({ id: isOrganizationMember ? "Cambiar miembro" : "Vincular miembro" })}>
              <IconButton
                size="small"
                onClick={() => setMode(mode === "member" ? null : "member")}
                color={mode === "member" ? "primary" : "default"}
              >
                <VerifiedUserIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isOrganizationMember && !requiresMembership && (
            <Tooltip title={intl.formatMessage({ id: "Usar datos manuales" })}>
              <IconButton
                size="small"
                onClick={() => setMode(mode === "manual" ? null : "manual")}
                color={mode === "manual" ? "primary" : "default"}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canSearchUsers && !hasRevaAccount && !isOrganizationMember && (
            <Tooltip title={intl.formatMessage({ id: "Vincular cuenta Reva" })}>
              <IconButton
                size="small"
                onClick={() => setMode(mode === "link" ? null : "link")}
                color={mode === "link" ? "primary" : "default"}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canSearchUsers && isAppUser && !isOrganizationMember && !requiresMembership && (
            <Tooltip title={intl.formatMessage({ id: "Desvincular Cuenta Reva" })}>
              <IconButton size="small" onClick={() => setConfirmUnlink(true)}>
                <LinkOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Collapse in={mode === "edit"} unmountOnExit>
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          {hasRevaAccount ? (
            <Alert severity="info">
              {intl.formatMessage({
                id: "Este jugador está vinculado a una cuenta Reva. Sus datos personales no pueden editarse desde esta inscripción.",
              })}
            </Alert>
          ) : (
            <Stack spacing={2}>
              <TextField
                size="small"
                label={intl.formatMessage({ id: "Nombre" })}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label={intl.formatMessage({ id: "Teléfono" })}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label={intl.formatMessage({ id: "Documento de identidad" })}
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                fullWidth
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={closeInline} disabled={saving}>
                  {intl.formatMessage({ id: "Cancelar" })}
                </Button>
                <Button variant="contained" onClick={savePlayer} disabled={saving}>
                  {saving ? <CircularProgress size={18} /> : intl.formatMessage({ id: "Guardar cambios" })}
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Collapse>

      <Collapse in={mode === "link"} unmountOnExit>
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <RevaText
            variant="caption"
            className={classes.metaRow}
            id="Introduce el número de teléfono o documento para buscar un usuario de Reva y vincularlo a este jugador."
            sx={{ mb: 1, display: "block" }}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              fullWidth
              label={intl.formatMessage({ id: "Teléfono" })}
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchUser()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={searchUser}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              fullWidth
              label={intl.formatMessage({ id: "Documento de identidad" })}
              value={searchDoc}
              onChange={(e) => setSearchDoc(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchUser()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={searchUser}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          {searching && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <CircularProgress size={20} />
            </Stack>
          )}
          {searchEmpty && !searching && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {intl.formatMessage({ id: "No se encontró un usuario Reva con esos datos" })}
            </Alert>
          )}
          {searchResult && (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
              <Avatar src={searchResult.full_profile_pic_url} />
              <RevaText sx={{ flex: 1 }}>{searchResult.full_name}</RevaText>
              <Button variant="contained" onClick={assignUser} disabled={saving}>
                {intl.formatMessage({ id: "Vincular usuario" })}
              </Button>
            </Stack>
          )}
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={closeInline}>{intl.formatMessage({ id: "Cancelar" })}</Button>
          </Stack>
        </Box>
      </Collapse>

      <Collapse in={mode === "member"} unmountOnExit>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Divider />
          <TournamentMemberSelector
            tournamentId={tournamentId}
            value={selectedMembers}
            onChange={setSelectedMembers}
            maxSelections={1}
            label={intl.formatMessage({ id: "Seleccionar miembro" })}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={closeInline}>{intl.formatMessage({ id: "Cancelar" })}</Button>
            <Button
              variant="contained"
              onClick={() => assignOrganizationMember()}
              disabled={!selectedMembers.length || saving}
            >
              {saving ? (
                <CircularProgress size={18} />
              ) : (
                intl.formatMessage({ id: isOrganizationMember ? "Cambiar miembro" : "Vincular miembro" })
              )}
            </Button>
          </Stack>
          {isOrganizationMember && !requiresMembership && (
            <Button size="small" onClick={() => setMode("manual")}>
              {intl.formatMessage({ id: "No encuentro al miembro; usar datos manuales" })}
            </Button>
          )}
        </Stack>
      </Collapse>

      {manualPlayerEditor}

      <AlertDialog
        hideButton
        isOpen={confirmUnlink}
        onConfirm={() => {
          setConfirmUnlink(false);
          unlinkRevaAccount();
        }}
        onCancel={() => setConfirmUnlink(false)}
        alertBody={intl.formatMessage({
          id: "¿Estás seguro de desvincular la cuenta de este jugador? Ya no podrá ver los resultados del torneo en su app.",
        })}
        confirmButtonTitle={intl.formatMessage({ id: "Sí, estoy seguro" })}
        cancelButtonTitle={intl.formatMessage({ id: "Volver atrás" })}
      />
      {inactiveMemberDialog}
    </Paper>
  );
};

const EditParticipantModal = ({
  participantId,
  tournament: initialTournament,
  hideTournamentSignUp,
  onSave,
  classes,
}) => {
  const intl = useIntl();
  const { user: authUser } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const [participant, setParticipant] = useState(null);
  const [ready, setReady] = useState(false);
  const [showNameEdit, setShowNameEdit] = useState(false);
  const [newName, setNewName] = useState("");
  const [sportCountries, setSportCountries] = useState([]);
  const [savingNationality, setSavingNationality] = useState(false);

  const [openChangeTournament, setOpenChangeTournament] = useState(false);
  const [openDeleteAlert, setOpenDeleteAlert] = useState(false);
  const [openRestoreAlert, setOpenRestoreAlert] = useState(false);
  const [openConfirmPay, setOpenConfirmPay] = useState(false);
  const [openProfilesModal, setOpenProfilesModal] = useState(false);
  const [openAddMember, setOpenAddMember] = useState(false);
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [addingMember, setAddingMember] = useState(false);
  const [confirmInactiveMemberAdd, setConfirmInactiveMemberAdd] = useState(false);

  const tournament = useMemo(
    () => ({ ...(initialTournament || {}), ...(participant?.tournament || {}) }),
    [initialTournament, participant?.tournament],
  );
  const isOrganizationTournament = Boolean(tournament?.organization_id || tournament?.competition?.organization_id);
  const canSearchUsers = Boolean(authUser?.permissions?.["players.search-user.assign"]);

  const fetchParticipant = useCallback(() => {
    performRequest("POST", participantsShowRoute, { participant_id: participantId }, true, authUser)
      .then((response) => {
        const data = response.data.data;
        setParticipant(data);
        setNewName(data.name);
        setShowNameEdit(false);
        setReady(true);
      })
      .catch((error) => {
        setReady(true);
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  }, [participantId, authUser, enqueueSnackbar]);

  useEffect(() => {
    fetchParticipant();
  }, [fetchParticipant]);

  useEffect(() => {
    let active = true;
    fetchSportCountries(intl.locale)
      .then((countries) => {
        if (active) setSportCountries(countries);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [intl.locale]);

  const saveNationality = (country) => {
    setSavingNationality(true);
    performRequest(
      "POST",
      participantsUpdateRoute,
      { participant_id: participant.id, nationality_code: country?.code ?? null },
      true,
      authUser,
    )
      .then((response) => {
        setParticipant({ ...participant, nationality_code: response.data.data.nationality_code });
        enqueueSnackbar(intl.formatMessage({ id: "Nacionalidad actualizada" }), { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      })
      .finally(() => setSavingNationality(false));
  };

  const saveName = () => {
    setShowNameEdit(false);
    performRequest("POST", participantsUpdateRoute, { participant_id: participant.id, name: newName }, true, authUser)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Jugador actualizado" }), { variant: "success" });
        fetchParticipant();
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const togglePaymentStatus = () => {
    const paidNow = signUp?.status === "completed";
    const newStatus = paidNow ? "waiting-payment" : "completed";
    performRequest(
      "POST",
      participantsUpdateRoute,
      { participant_id: participant.id, status: newStatus },
      true,
      authUser,
    )
      .then((response) => {
        const updated = response.data.data;
        enqueueSnackbar(
          intl.formatMessage({
            id: newStatus === "completed" ? "Inscripción marcada como pagada" : "Inscripción marcada como pendiente",
          }),
          { variant: "success" },
        );
        setParticipant({ ...participant, ...updated });
        if (onSave) onSave(updated);
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const addPlayer = () => {
    setReady(false);
    performRequest(
      "POST",
      participantsAddSeedPlayerRoute,
      {
        amount: 1,
        tournament_id: tournament.id,
        participant_id: participant.id,
      },
      true,
      authUser,
    )
      .then(() => fetchParticipant())
      .catch((error) => {
        setReady(true);
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const addOrganizationMember = async (allowInactiveMember = false) => {
    const member = membersToAdd[0];
    if (!member) return;

    if (!allowInactiveMember && !member.is_active) {
      setConfirmInactiveMemberAdd(true);
      return;
    }

    setAddingMember(true);
    setConfirmInactiveMemberAdd(false);
    try {
      await performRequest(
        "POST",
        participantsAssignOrganizationMemberRoute,
        {
          participant_id: participant.id,
          player_id: null,
          organization_member_id: member.organization_member_id,
          allow_inactive_members: allowInactiveMember,
        },
        true,
        authUser,
      );
      enqueueSnackbar(intl.formatMessage({ id: "Miembro agregado al equipo" }), { variant: "success" });
      setMembersToAdd([]);
      setOpenAddMember(false);
      await fetchParticipant();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "No se pudo agregar el miembro.", { variant: "error" });
    } finally {
      setAddingMember(false);
    }
  };

  const handleDelete = () => {
    performRequest("POST", participantsDeleteRoute, { participant_id: participant.id }, true, authUser)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Participante eliminado con éxito" }), {
          variant: "success",
        });
        if (onSave) onSave();
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const handleRestoreWithdrawn = () => {
    performRequest("POST", participantsRestoreWithdrawnRoute, { participant_id: participant.id }, true, authUser)
      .then((response) => {
        enqueueSnackbar(response?.data?.message || intl.formatMessage({ id: "Participante recuperado con éxito" }), {
          variant: "success",
        });
        fetchParticipant();
        if (onSave) onSave();
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  };

  const openPaymentsLink = (id) => {
    window.open(`${baseDomain}/admin/online-payments/${id}`, "_blank");
  };

  const signUp = participant?.tournament_sign_up;
  const paidOnline = Boolean(signUp?.payment_id);
  const isPaid = signUp?.status === "completed" || paidOnline;
  const withdrawnAfterStart = Boolean(participant?.withdrawn_after_start);
  const tournamentStarted = tournament && tournament.state !== TOURNAMENT_PENDING;
  const tournamentFinished =
    tournament?.state === TOURNAMENT_FINISHED || tournament?.state === TOURNAMENT_PENDING_FINISHED;
  const playersPerTeam = tournament?.players_per_team || 1;

  const normalizedPlayers = useMemo(() => {
    if (!participant) return [];

    const rawPlayers = participant?.team?.players || [];
    const directUserData =
      participant?.user?.data ||
      participant?.user ||
      participant?.tournament_sign_up?.user?.data ||
      participant?.tournament_sign_up?.user;

    if (rawPlayers.length > 0) return rawPlayers;

    if (directUserData) {
      return [
        {
          ...directUserData,
          id: directUserData.id || directUserData.user_id,
          name: directUserData.full_name,
          phone_number: directUserData.cellphone_number,
          playerable_type: "App\\User",
          playerable_id: directUserData.id || directUserData.user_id,
          is_user_fallback: true, // Flag para PlayerCard
        },
      ];
    }

    return [];
  }, [participant]);

  const showAddPlayer = useMemo(() => {
    return participant?.team && participant.team.is_couple === false && normalizedPlayers.length < playersPerTeam;
  }, [participant, normalizedPlayers.length, playersPerTeam]);

  const surnameSuggestion = useMemo(() => suggestSurnameName(normalizedPlayers), [normalizedPlayers]);

  if (!ready || !participant) {
    return (
      <Grid container className={classes.modalContainer} justifyContent="center">
        <CircularProgress size={24} />
      </Grid>
    );
  }

  const nationalityCode = participant.nationality_code;
  const selectedNationality = nationalityCode
    ? sportCountries.find((country) => country.code === nationalityCode) || { code: nationalityCode, name: "" }
    : null;

  const showDeleteButton = !tournamentStarted || (tournamentStarted && !withdrawnAfterStart && !tournamentFinished);
  const deleteButtonLabel = tournamentStarted
    ? intl.formatMessage({ id: "Retirar del torneo" })
    : intl.formatMessage({ id: "Eliminar inscripción" });
  const deleteAlertBody = tournamentStarted
    ? intl.formatMessage({
        id: "Si retiras a este participante después de iniciado el torneo, no se eliminarán sus partidos ya creados. El participante dejará de jugar y sus cruces pendientes se resolverán por walkover.",
      })
    : intl.formatMessage({
        id: "¿Estás seguro de que quieres eliminar a este participante del torneo? Asegúrate de haberlo conversado previamente.",
      });

  const renderHeader = () => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      className={classes.section}
    >
      <Stack spacing={0.5}>
        <RevaText id="Gestionar participante" variant="h5" sx={{ fontWeight: 600 }} />
        <RevaText variant="caption" className={classes.metaRow}>
          {tournament?.name}
          {participant.created_at &&
            ` · ${intl.formatMessage({ id: "Inscripto el" })} ${moment(participant.created_at).format(
              "DD/MM/YYYY HH:mm",
            )}`}
        </RevaText>
      </Stack>

      {(withdrawnAfterStart || (participant?.ranking_points !== undefined && participant?.ranking_points !== null)) && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {withdrawnAfterStart && <Chip size="small" color="warning" label={intl.formatMessage({ id: "Retirado" })} />}
          {participant?.ranking_points !== undefined && participant?.ranking_points !== null && (
            <Chip
              size="small"
              color="secondary"
              variant="outlined"
              icon={<EmojiEventsIcon />}
              label={`${intl.formatMessage({ id: "Puntos de ranking" })}: ${participant.ranking_points}`}
            />
          )}
        </Stack>
      )}
    </Stack>
  );

  const renderSummary = () => (
    <Stack
      className={classes.section}
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ sm: "center" }}
      spacing={1}
    >
      <Autocomplete
        size="small"
        options={sportCountries}
        value={selectedNationality}
        disabled={savingNationality}
        onChange={(event, country) => saveNationality(country)}
        getOptionLabel={(country) => country.code}
        renderOption={(props, country) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              {`${country.code} · ${country.name}`}
            </li>
          );
        }}
        filterOptions={filterNationalities}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        renderInput={(params) => <TextField {...params} label={intl.formatMessage({ id: "Nacionalidad" })} />}
        slotProps={{ popper: { sx: { minWidth: 260 } } }}
        sx={{ width: { xs: "100%", sm: 120 }, flexShrink: 0 }}
      />
      <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          disabled={!showNameEdit}
          label={intl.formatMessage({ id: "Nombre completo" })}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          sx={{ flex: "1 1 220px", minWidth: 0 }}
        />
        {showNameEdit ? (
          <>
            <Button onClick={saveName}>{intl.formatMessage({ id: "Guardar" })}</Button>
            <Button
              onClick={() => {
                setShowNameEdit(false);
                setNewName(participant.name);
              }}
            >
              {intl.formatMessage({ id: "Cancelar" })}
            </Button>
          </>
        ) : (
          <IconButton size="small" onClick={() => setShowNameEdit(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {surnameSuggestion && surnameSuggestion !== newName && (
          <Tooltip
            title={intl.formatMessage({
              id: "Propone un nombre con los apellidos de los jugadores. Revísalo antes de guardar.",
            })}
          >
            <Button
              size="small"
              onClick={() => {
                setNewName(surnameSuggestion);
                setShowNameEdit(true);
              }}
            >
              {intl.formatMessage({ id: "Usar apellidos" })}
            </Button>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );

  const renderPlayers = () => (
    <Stack className={classes.section} spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap gap={1}>
        <RevaText className={classes.sectionTitle} id="Jugadores" />
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={() => setOpenProfilesModal(true)}>
            {intl.formatMessage({ id: "Ver historial de torneos" })}
          </Button>
          {showAddPlayer && (
            <Button
              size="small"
              onClick={() => (isOrganizationTournament ? setOpenAddMember((open) => !open) : addPlayer())}
            >
              {intl.formatMessage({ id: "Agregar un jugador" })}
            </Button>
          )}
        </Stack>
      </Stack>

      <Collapse in={openAddMember} unmountOnExit>
        <Stack spacing={1.5}>
          <Alert severity="info">
            {intl.formatMessage({
              id: tournament?.requires_membership_to_join_tournaments
                ? "Este torneo exige seleccionar un miembro de la organización."
                : "Este torneo pertenece a una organización. Puedes seleccionar un miembro o agregar un jugador manual.",
            })}
          </Alert>
          <TournamentMemberSelector
            tournamentId={tournament?.id}
            value={membersToAdd}
            onChange={setMembersToAdd}
            maxSelections={1}
            label={intl.formatMessage({ id: "Seleccionar miembro" })}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={() => setOpenAddMember(false)} disabled={addingMember}>
              {intl.formatMessage({ id: "Cancelar" })}
            </Button>
            <Button
              variant="contained"
              onClick={() => addOrganizationMember()}
              disabled={!membersToAdd.length || addingMember}
            >
              {addingMember ? <CircularProgress size={18} /> : intl.formatMessage({ id: "Agregar miembro" })}
            </Button>
            {!tournament?.requires_membership_to_join_tournaments && (
              <Button
                onClick={() => {
                  setOpenAddMember(false);
                  addPlayer();
                }}
                disabled={addingMember}
              >
                {intl.formatMessage({ id: "Agregar jugador manualmente" })}
              </Button>
            )}
          </Stack>
        </Stack>
      </Collapse>

      {participant.team && normalizedPlayers.length > 1 && playersPerTeam > 2 && (
        <RevaText variant="caption" className={classes.metaRow}>
          {`${normalizedPlayers.length} / ${playersPerTeam}`}
        </RevaText>
      )}

      <Stack spacing={1.5}>
        {/* Caso especial de singles donde está vacío por completo */}
        {normalizedPlayers.length === 0 && tournament?.modality === "singles" ? (
          <PlayerCard
            player={{ name: participant.name, full_profile_pic_url: null }}
            participant={participant}
            canSearchUsers={canSearchUsers}
            onPlayerUpdated={fetchParticipant}
            classes={classes}
            isSingles={true}
            NoUser={true}
            isOrganizationTournament={isOrganizationTournament}
            requiresMembership={Boolean(tournament?.requires_membership_to_join_tournaments)}
            tournamentId={tournament?.id}
          />
        ) : (
          // Flujo unificado sin romper las reglas de React
          normalizedPlayers.map((player) => (
            <PlayerCard
              key={player.id}
              player={player}
              participant={participant}
              canSearchUsers={canSearchUsers}
              onPlayerUpdated={fetchParticipant}
              classes={classes}
              isSingles={tournament?.modality === "singles"}
              NoUser={false}
              isOrganizationTournament={isOrganizationTournament}
              requiresMembership={Boolean(tournament?.requires_membership_to_join_tournaments)}
              tournamentId={tournament?.id}
            />
          ))
        )}
      </Stack>
    </Stack>
  );

  const renderSignUp = () => {
    if (hideTournamentSignUp || !signUp) return null;
    const signUpCreator = signUp.user?.data || signUp.user;
    const paymentTypeLabel = signUp.payment_id
      ? intl.formatMessage({ id: "Pago online" })
      : intl.formatMessage({ id: "Efectivo / Otro" });

    return (
      <Stack className={classes.section} spacing={1}>
        <RevaText className={classes.sectionTitle} id="Inscripción y pago" />
        <Grid container spacing={1.5}>
          {tournament?.name && (
            <Grid item xs={12} sm={3}>
              <RevaText variant="caption" className={classes.metaRow} id="Torneo" />
              <RevaText>{tournament.name}</RevaText>
            </Grid>
          )}
          <Grid item xs={12} sm={3}>
            <RevaText variant="caption" className={classes.metaRow} id="Tipo de pago" />
            <Stack direction="row" spacing={1} alignItems="center">
              <RevaText>{paymentTypeLabel}</RevaText>
              {signUp.payment_id && (
                <Button size="small" onClick={() => openPaymentsLink(signUp.payment_id)}>
                  {intl.formatMessage({ id: "Ver comprobante" })}
                </Button>
              )}
            </Stack>
          </Grid>
          {signUp.created_at && (
            <Grid item xs={12} sm={3}>
              <RevaText variant="caption" className={classes.metaRow} id="Fecha de inscripción" />
              <RevaText>{moment(signUp.created_at).format("DD/MM/YYYY HH:mm")}</RevaText>
            </Grid>
          )}
          <Grid item xs={12} sm={3}>
            <RevaText variant="caption" className={classes.metaRow} id="Estado de pago" />
            <Stack spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
              <PaymentStatusChip status={signUp?.status} paidOnline={paidOnline} />
              {!paidOnline && (
                <Button size="small" variant="outlined" onClick={() => setOpenConfirmPay(true)}>
                  {isPaid
                    ? intl.formatMessage({ id: "Marcar como pendiente de pago" })
                    : intl.formatMessage({ id: "Marcar como pagado" })}
                </Button>
              )}
            </Stack>
          </Grid>
          {signUpCreator && !signUp.is_admin && (
            <Grid item xs={12}>
              <RevaText variant="caption" className={classes.metaRow} id="Creada por" />
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <RevaText>{signUpCreator.full_name || signUpCreator.email}</RevaText>
                {signUpCreator.cellphone_number && (
                  <>
                    <IconButton size="small" component="a" href={`tel:${signUpCreator.cellphone_number}`}>
                      <CallIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      component="a"
                      href={`https://wa.me/${signUpCreator.cellphone_number.replace("+", "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>
            </Grid>
          )}
        </Grid>
      </Stack>
    );
  };

  const renderAdminActions = () => (
    <Stack className={classes.section} spacing={1.5}>
      <RevaText className={classes.sectionTitle} id="Acciones administrativas" />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {withdrawnAfterStart && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<RestoreIcon />}
            onClick={() => setOpenRestoreAlert(true)}
          >
            {intl.formatMessage({ id: "Recuperar participante" })}
          </Button>
        )}
        {showDeleteButton && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setOpenDeleteAlert(true)}
          >
            {deleteButtonLabel}
          </Button>
        )}
        <Button size="small" variant="outlined" color="action" onClick={() => setOpenChangeTournament(true)}>
          {intl.formatMessage({ id: "Mover a otro torneo" })}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <div className={classes.modalContainer}>
      {renderHeader()}
      {renderSummary()}
      <Divider sx={{ my: 1.5 }} />
      {renderPlayers()}
      {!hideTournamentSignUp && signUp && (
        <>
          <Divider sx={{ my: 1.5 }} />
          {renderSignUp()}
        </>
      )}
      <Divider sx={{ my: 1.5 }} />
      {renderAdminActions()}

      <Dialog open={openProfilesModal} onClose={() => setOpenProfilesModal(false)} fullWidth maxWidth="lg">
        <ProfilesModal participant={participant} editParticipant={() => setOpenProfilesModal(false)} />
      </Dialog>

      <Dialog
        open={openChangeTournament}
        onClose={() => setOpenChangeTournament(false)}
        disableAutoFocus
        fullWidth
        maxWidth="md"
      >
        <ChangeTournamentModal
          competitionId={participant.tournament?.competition_id}
          participant={participant}
          onSave={() => {
            setOpenChangeTournament(false);
            fetchParticipant();
            if (onSave) onSave();
          }}
        />
      </Dialog>

      <AlertDialog
        hideButton
        isOpen={openDeleteAlert}
        onConfirm={() => {
          setOpenDeleteAlert(false);
          handleDelete();
        }}
        onCancel={() => setOpenDeleteAlert(false)}
        alertBody={deleteAlertBody}
        confirmButtonTitle={intl.formatMessage({ id: "Sí, estoy seguro" })}
        cancelButtonTitle={intl.formatMessage({ id: "Volver atrás" })}
      />

      <AlertDialog
        hideButton
        isOpen={openRestoreAlert}
        onConfirm={() => {
          setOpenRestoreAlert(false);
          handleRestoreWithdrawn();
        }}
        onCancel={() => setOpenRestoreAlert(false)}
        alertBody={intl.formatMessage({
          id: "¿Quieres recuperar a este participante? El sistema validará si puede volver al torneo y, si no es posible, te mostrará el motivo.",
        })}
        confirmButtonTitle={intl.formatMessage({ id: "Recuperar" })}
        cancelButtonTitle={intl.formatMessage({ id: "Cancelar" })}
      />

      <AlertDialog
        hideButton
        isOpen={openConfirmPay}
        onConfirm={() => {
          setOpenConfirmPay(false);
          togglePaymentStatus();
        }}
        onCancel={() => setOpenConfirmPay(false)}
        alertBody={
          isPaid
            ? intl.formatMessage({ id: "¿Marcar esta inscripción como pendiente de pago?" })
            : intl.formatMessage({ id: "¿Confirmar el pago de esta inscripción?" })
        }
        confirmButtonTitle={intl.formatMessage({ id: "Sí, estoy seguro" })}
        cancelButtonTitle={intl.formatMessage({ id: "Volver atrás" })}
      />

      <AlertDialog
        hideButton
        isOpen={confirmInactiveMemberAdd}
        onConfirm={() => addOrganizationMember(true)}
        onCancel={() => setConfirmInactiveMemberAdd(false)}
        alertTitle={intl.formatMessage({ id: "Miembro inactivo" })}
        alertBody={intl.formatMessage({
          id: "El miembro seleccionado está inactivo. ¿Deseas agregarlo de todas formas?",
        })}
        confirmButtonTitle={intl.formatMessage({ id: "Agregar igualmente" })}
        cancelButtonTitle={intl.formatMessage({ id: "Cancelar" })}
      />
    </div>
  );
};

export default withStyles(styles)(EditParticipantModal);
